import React, { useState, useEffect } from "react";
import { get, post } from "../../../services/api";
import { toast } from "react-toastify";

const formatAmount = (num) => {
  if (num === undefined || num === null || num === "") return "0";
  return Number(num).toLocaleString();
};

const StudentManualFeeTab = ({ selectedStudent }) => {
  const [loading, setLoading] = useState(false);
  const [concessionData, setConcessionData] = useState([]);
  const [feeElements, setFeeElements] = useState([]);
  const [concessionTypeId, setConcessionTypeId] = useState("");
  const [studentInfo, setStudentInfo] = useState(null);

  const centerId = localStorage.getItem("centerId");
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");
  const userId = localStorage.getItem("userId");

  // Fetch basic student info needed for fee plan
  const fetchStudentProfile = async () => {
    try {
      setLoading(true);
      const response = await get(
        `InspStudentFirstRegistrtion/GetStudentPersonalProfileInfo/${selectedStudent.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.isSuccess && response.studentPersonalInfoDto) {
        const studentData = response.studentPersonalInfoDto;
        setStudentInfo(studentData);
        setConcessionTypeId(studentData.centerConcessionId && studentData.centerConcessionId != 0 ? studentData.centerConcessionId.toString() : "");
        if (studentData.classId) {
          const fetchedRegId = studentData.firstRegistrationId || studentData.id || selectedStudent.studentFirstRegistrationId || selectedStudent.id;
          await fetchFeePlan(studentData.classId, fetchedRegId);
        }
      }
    } catch (error) {
      console.error("Error fetching student personal profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConcession = async () => {
    try {
      const response = await get(
        `ConcessionType/GetCenterConcessionByCenterId?centerId=${centerId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const groupedOptions = response.concession.map((group) => ({
        label: group.label,
        options: group.concessionDiscounts.map((concession) => ({
          value: concession.concessionTypeId,
          label: concession.concessionTypeName,
        })),
      }));
      setConcessionData(groupedOptions);
    } catch (err) {
      console.error("Error fetching concession:", err);
    }
  };

  const fetchFeePlan = async (classId, regId, concessionId = 0) => {
    if (!classId) return;
    try {
      setLoading(true);
      // Fetch mapped fee plan for this existing student
      let url = `FeePlan/GetFeePlanCenterClassId?centerId=${centerId}&centerClassId=${classId}&regId=${regId}`;
      if (concessionId) url += `&concessionId=${concessionId}`;
      
      const response = await get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response && response.isSuccess) {
        const mappedFees = response.classes.map((fee) => {
          const defaultAmt = fee.defaultAmount || 0;
          const apiChargeableAmt = fee.chargeableAmount !== undefined && fee.chargeableAmount !== null ? fee.chargeableAmount : fee.chargebaleAmount;
          
          const chargeableAmt = apiChargeableAmt !== undefined && apiChargeableAmt !== null ? apiChargeableAmt : defaultAmt;
          const discountAmt = fee.discount !== undefined && fee.discount !== null ? fee.discount : (defaultAmt - chargeableAmt);
          
          // Safely parse boolean to avoid string "false" causing truthy values
          let apiPrintOnChallan = fee.printDefaultFeeOnChallan;
          if (apiPrintOnChallan === "false") apiPrintOnChallan = false;
          if (apiPrintOnChallan === "true") apiPrintOnChallan = true;

          const finalPrintDefault = apiPrintOnChallan !== undefined && apiPrintOnChallan !== null 
              ? !!apiPrintOnChallan 
              : (discountAmt <= 0);

          return {
            ...fee,
            printDefaultFeeOnChallan: finalPrintDefault,
            id: fee.id || 0,
            feeElementId: fee.feeElementId || 0,
            chargebaleAmount: chargeableAmt,
            discount: discountAmt
          };
        });
        setFeeElements(mappedFees);
      }
    } catch (err) {
      console.error("Error fetching fee plan:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStudent?.id && centerId) {
      fetchConcession();
      fetchStudentProfile();
    }
  }, [selectedStudent, centerId]);

  const handleChargeableAmountChange = (e, feeIndex) => {
    const valStr = e.target.value.toString().replace(/,/g, '');
    const val = valStr === "" ? "" : parseFloat(valStr);
    const newFeeElements = [...feeElements];
    const fee = newFeeElements[feeIndex];
    fee.chargebaleAmount = val;
    const discount = (fee.defaultAmount || 0) - (val === "" ? 0 : val);
    fee.discount = discount;
    
    // Automatically uncheck if there is a discount greater than zero
    if (discount > 0) {
      fee.printDefaultFeeOnChallan = false;
    } else {
      fee.printDefaultFeeOnChallan = true;
    }
    setFeeElements(newFeeElements);
  };

  const handlePrintDefaultFeeChange = (e, feeIndex) => {
    const { checked } = e.target;
    const newFeeElements = [...feeElements];
    newFeeElements[feeIndex].printDefaultFeeOnChallan = checked;
    setFeeElements(newFeeElements);
  };

  const handleConcessionChange = (e) => {
    const value = e.target.value;
    setConcessionTypeId(value);
    
    // Refresh fee plan when concession is selected
    if (studentInfo?.classId) {
      const regId = selectedStudent.studentFirstRegistrationId || selectedStudent.id;
      fetchFeePlan(studentInfo.classId, regId, value || 0);
    }
  };

  const handleSave = async () => {
    if (!studentInfo) return;

    try {
      setLoading(true);
      const currentTime = new Date().toISOString();
      const regId = selectedStudent.studentFirstRegistrationId || selectedStudent.id;

      // Note: Since no separate Fee Update API was specific, updating through the main Create API
      // as used in StudentInquiry. Alternatively, we send the minimal required fields.
      const studentPayload = {
        id: regId,
        inspStudentFamilyInfoId: studentInfo.familyInfoId || 0,
        applyingForSession: studentInfo.applyingForSession || 0,
        classId: parseInt(studentInfo.classId) || 0,
        centerConcessionId: parseInt(concessionTypeId) || 0,
        entryTermId: parseInt(studentInfo.entryTermId) || 0,
        studentFullName: studentInfo.studentFullName || "",
        dob: studentInfo.dob ? new Date(studentInfo.dob).toISOString() : currentTime,
        genderId: parseInt(studentInfo.genderId) || 1,
        languageId: parseInt(studentInfo.languageId) || 1,
        centerId: parseInt(centerId),
        code: studentInfo.code || "",
        isDelete: false,
        createdOn: currentTime,
        createdBy: userId || "system",
        updatedOn: currentTime,
        updatedBy: userId || "system",
        attachments: [], // We don't update attachments from here
        guardianIdentityNo: studentInfo.guardianIdentityNo || "",
        isDataImported: false,
        feeDetails: feeElements.map(fee => ({
          id: fee.id || 0,
          studentFirstRegistrationId: regId,
          feeElementId: fee.feeElementId || 0,
          feeElement: fee.feeElement || "",
          printDefaultFeeOnChallan: fee.printDefaultFeeOnChallan || false,
          defaultAmount: fee.defaultAmount || 0,
          chargeableAmount: fee.chargebaleAmount || 0,
          discount: fee.discount !== undefined ? fee.discount : (fee.defaultAmount - (fee.chargebaleAmount || 0))
        }))
      };

      const studentResponse = await post(
        "InspStudentFirstRegistrtion/Create",
        studentPayload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (studentResponse.isSuccess) {
        toast.success("Manual Fee Section saved successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error(studentResponse.message || "Failed to save data.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (err) {
      console.error("Error saving data:", err);
      toast.error("An error occurred while saving data.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-content-area d-flex flex-column h-100">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0 fw-bold">Manual Fee Section</h5>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={loading}
          style={{ width: "120px" }}
        >
          {loading ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : (
            <>
              <i className="fas fa-save me-2"></i> Save
            </>
          )}
        </button>
      </div>

      <div className="profile-scrollable-area p-1 flex-grow-1">
        <div className="mb-4">
          <div>
            <div className="row mb-4">
              <div className="col-md-6 col-lg-4">
                {roleId === "3" && (
                  <div className="mb-2">
                    <label className="form-label fw-medium mb-1" style={{ color: "#475569", fontSize: "13px" }}>
                      Concession
                    </label>
                    <select
                      className="form-select"
                      style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px" }}
                      value={concessionTypeId}
                      onChange={handleConcessionChange}
                    >
                      <option value="">Select Concession</option>
                      {concessionData.map((group, groupIdx) => (
                        <optgroup key={groupIdx} label={group.label}>
                          {group.options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {/* <div className="col-md-6 col-lg-8 d-flex align-items-center">
                <h6 className="mb-0 fw-semibold text-primary">
                  Fee Plan: {feeElements?.length > 0 ? feeElements[0].feePlanName : "N/A"}
                </h6>
              </div> */}
            </div>

            <div className="row px-2">
              <div className="col-12">
                <div className="table-responsive rounded-3 border" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                  <table className="table table-hover align-middle mb-0" style={{ fontSize: "14px" }}>
                    <thead className="table-light" style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                      <tr>
                        <th className="fw-semibold text-secondary py-3 px-4" style={{ width: "30%" }}>Fee Elements</th>
                        <th className="fw-semibold text-secondary text-end py-3 px-4">Default Amount</th>
                        <th className="fw-semibold text-secondary text-center py-3 px-4" style={{ width: "20%" }}>Chargeable Amount</th>
                        <th className="fw-semibold text-secondary text-end py-3 px-4">Discount Given</th>
                        <th className="fw-semibold text-secondary text-center py-3 px-4">Print Default Amount on Challan</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {loading && feeElements.length === 0 ? (
                         <tr>
                            <td colSpan="5" className="text-center py-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </td>
                         </tr>
                      ) : feeElements?.length > 0 ? (
                        feeElements.map((fee, feeIndex) => {
                          const isFixedFee = 
                            fee.feeElement?.toLowerCase().includes("admission") || 
                            fee.feeElement?.toLowerCase().includes("registration");

                          return (
                            <tr key={feeIndex} className="border-bottom">
                              <td className="py-3 px-4 text-dark fw-medium">{fee.feeElement}</td>
                              <td className="py-3 px-4 text-end text-muted">{formatAmount(fee.defaultAmount)}</td>
                              <td className="py-3 px-4">
                                <input
                                  type="text"
                                  className="form-control text-center shadow-sm"
                                  style={{ borderRadius: "6px", border: "1px solid #cbd5e1", transition: "all 0.2s" }}
                                  value={formatAmount(fee.chargebaleAmount)}
                                  onChange={(e) => handleChargeableAmountChange(e, feeIndex)}
                                  disabled={(!!concessionTypeId && concessionTypeId !== "0") || isFixedFee}
                                  onFocus={(e) => (e.target.value === '0' || e.target.value === '') && (e.target.value = '')}
                                />
                              </td>
                              <td className="py-3 px-4 text-end">
                                <span className="badge rounded-pill" style={{ backgroundColor: "#dcfce7", color: "#166534", padding: "6px 12px", fontSize: "13px", fontWeight: "600" }}>
                                  {formatAmount(fee.discount !== undefined ? fee.discount : (fee.defaultAmount - (fee.chargebaleAmount || 0)))}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <div className="form-check d-flex justify-content-center m-0">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      style={{ width: "18px", height: "18px", cursor: "pointer", borderColor: "#94a3b8" }}
                                      checked={!!fee.printDefaultFeeOnChallan}
                                      onChange={(e) => handlePrintDefaultFeeChange(e, feeIndex)}
                                      disabled={isFixedFee}
                                    />
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-5">
                            <i className="fas fa-search-dollar fa-2x mb-3 text-light d-block" style={{ color: "#cbd5e1" }}></i>
                            No fee elements found. Please select a class to view applicable fees.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentManualFeeTab;
